import React, {createContext, Suspense, useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import 'antd-mobile/bundle/style.css';
import './index.css';
import './styles/domain-styles.css';  // 使用普通CSS实现域名样式
import './utils/domainInit';  // 导入域名初始化脚本
import reportWebVitals from './reportWebVitals';
import { isMobile } from './utils/isMobile';
import { setupChunkLoadRecovery } from './utils/chunkLoadRecovery';
import BookingRouteShell from './pages/booking-platform/BookingRouteShell';
import { TeacherPortalI18nProvider } from './pages/booking-platform/utils/teacherPortalI18n';
import ShareRecorded from './pages/share_recorded';
import AboutApp from './pages/about_app';
import PriceUp from './pages/price_up';
import SpaceUpgrade from './pages/space_upgrade';
import ShareWord from './pages/share_word';
import ExpandSpace from './pages/expand_space';
import {JsBridge} from "./utils/JsBridge";
import {isApp} from "./utils/EnvUtils";
import {DeerUser} from "./typings/DeerUser";
import { IS_HUIYA } from './config/domain';
import ErrorBoundary from './components/ErrorBoundary';

const H5Website = React.lazy(() => import('./pages/h5_website'));
const Website = React.lazy(() => import('./pages/website'));
const H5SelfService = React.lazy(() => import('./pages/h5_self_service'));
const H5SelfServiceCreate = React.lazy(() => import('./pages/h5_self_service_create'));
const VipSale = React.lazy(() => import('./pages/h5_vip_sale'));
const TvVipSale = React.lazy(() => import('./pages/tv_vip_sale'));
const BaiduPan = React.lazy(() => import('./pages/h5_self_service_baidu'));
const ShareCourse = React.lazy(() => import('./pages/share_course'));
const ShareCourseOutside = React.lazy(
  () => import('./pages/share_course_outside')
);
const VipShare = React.lazy(() => import('./pages/vip_share'));
const CumulativeInvitation = React.lazy(
  () => import('./pages/cumulative_invitation')
);
const ReceiveReward = React.lazy(() => import('./pages/receive_reward'));
const AccountDetails = React.lazy(() => import('./pages/account_details'));
const ActivityDescription = React.lazy(
  () => import('./pages/activity_description')
);
const DailyPaper = React.lazy(() => import('./pages/daily_paper'));
const LearningReportDetail = React.lazy(
  () => import('./pages/learning_report_detail')
);
const VipShareActivity = React.lazy(() => import('./pages/vip_share_activity'));
const GreatTracher = React.lazy(() => import('./pages/great_teacher'));
const FormTeacher = React.lazy(() => import('./pages/form_teacher'));
const NYSpecial = React.lazy(() => import('./pages/ny_special'));
const RegularActivity = React.lazy(() => import('./pages/regular_activity'));
const PriceDiffPay = React.lazy(() => import('./pages/price_diffPay'));
const PriceDiffPayHy = React.lazy(() => import('./pages/price_diffPay_hy'));
const LearningReport = React.lazy(() => import('./pages/learning_report'));
const TvDownload = React.lazy(() => import('./pages/tv_download'));
const BookingPlatform = React.lazy(() => import('./pages/booking-platform'));
const FilipinoIntro = React.lazy(() => import('./pages/booking-platform/intro/index'));
const TeacherDetail = React.lazy(() => import('./pages/booking-platform/teacher-detail'));
const TeacherEdit = React.lazy(() => import('./pages/booking-platform/teacher-edit'));
const TeacherAdd = React.lazy(() => import('./pages/booking-platform/teacher-add'));
const StudentEdit = React.lazy(() => import('./pages/booking-platform/student-edit'));
const TextbookSelect = React.lazy(() => import('./pages/booking-platform/textbook-select'));
const BookDetail = React.lazy(() => import('./pages/booking-platform/book-detail'));
const BookingConfirm = React.lazy(() => import('./pages/booking-platform/booking-confirm'));
const BookingLogin = React.lazy(() => import('./pages/booking-platform/login'));
const FavoritesPage = React.lazy(() => import('./pages/booking-platform/favorites'));
const FeedbackPage = React.lazy(() => import('./pages/booking-platform/feedback'));
const CourseReportPage = React.lazy(() => import('./pages/booking-platform/course-report'));
const CourseReportSharePage = React.lazy(() => import('./pages/booking-platform/course-report-share'));
const LearningTasksPage = React.lazy(() => import('./pages/booking-platform/learning-tasks'));
const HelpCenterPage = React.lazy(() => import('./pages/booking-platform/help-center'));
const TeacherHelpCenterPage = React.lazy(() => import('./pages/booking-platform/teacher-help-center'));
const TeacherSystemGuideVideoPage = React.lazy(() => import('./pages/booking-platform/teacher-system-guide-video'));
const TeacherRulesEnPage = React.lazy(() => import('./pages/booking-platform/teacher-rules-en'));
const OralPracticeIntroPage = React.lazy(() => import('./pages/booking-platform/oral-practice-intro'));
const ManagedTeachersPage = React.lazy(() => import('./pages/booking-platform/managed-teachers'));
const TeacherManagementDetailPage = React.lazy(() => import('./pages/booking-platform/teacher-management-detail'));
const TeacherSchedulePage = React.lazy(() => import('./pages/booking-platform/teacher-schedule'));
const PointsRecordPage = React.lazy(() => import('./pages/booking-platform/points-record'));
const AdminCoursesPage = React.lazy(() => import('./pages/booking-platform/admin-courses'));
const RechargePage = React.lazy(() => import('./pages/booking-platform/recharge'));
const PlatformAdminPage = React.lazy(() => import('./pages/booking-platform/platform-admin'));
const WelfareAdminPage = React.lazy(() => import('./pages/booking-platform/welfare-admin'));
const UserInsightPage = React.lazy(() => import('./pages/booking-platform/user-insight'));
const CourseMaterialSharePage = React.lazy(() => import('./pages/booking-platform/course-material-share'));
const TeacherPortalLogin = React.lazy(() => import('./pages/booking-platform/teacher-portal-login'));
const TeacherPortalPage = React.lazy(() => import('./pages/booking-platform/teacher-portal'));
const TeacherCourseDetailPage = React.lazy(() => import('./pages/booking-platform/teacher-course-detail'));
const RecurringBookingPage = React.lazy(() => import('./pages/booking-platform/recurring-booking'));
const AdminRecurringBookingPage = React.lazy(() => import('./pages/booking-platform/admin-recurring-booking'));
const InteractiveWordCard = React.lazy(() => import('./pages/interactive_word_card'));
const RazInteractive = React.lazy(() => import('./pages/raz_interactive'));

const isTvDownloadHost = window.location.hostname === 'tv.deerbook.cn';

const withBookingShell = (element: React.ReactNode) => (
  <BookingRouteShell>{element}</BookingRouteShell>
);

const withTeacherPortalShell = (element: React.ReactNode) => (
  <BookingRouteShell>
    <TeacherPortalI18nProvider>{element}</TeacherPortalI18nProvider>
  </BookingRouteShell>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: isTvDownloadHost ? <TvDownload /> : isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/wechat',
    element: isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/app',
    element: isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/self_service',
    element: <H5SelfService />,
  },
  {
    path: '/self_service_create',
    element: <H5SelfServiceCreate />,
  },
  {
    path: '/self_service_baidu',
    element: <BaiduPan />,
  },
  {
    path: '/share_course',
    element: <ShareCourse />,
  },
  {
    path: '/share_course_outside',
    element: <ShareCourseOutside />,
  },
  {
    path: '/vip_sale',
    element: <VipSale />,
  },
  {
    path: '/tv_vip_sale',
    element: <TvVipSale />,
  },
  {
    path: '/vip_share',
    element: <VipShare />,
  },
  {
    path: '/vip_share_activity',
    element: IS_HUIYA ? <Navigate to="/" replace /> : <VipShareActivity />,
  },
  {
    path: '/cumulative_invitation',
    element: <CumulativeInvitation />,
  },
  {
    path: '/receive_reward',
    element: <ReceiveReward />,
  },
  {
    path: '/account_details',
    element: <AccountDetails />,
  },
  {
    path: '/activity_description',
    element: <ActivityDescription />,
  },
  {
    path: '/recorded_share',
    element: <ShareRecorded />,
  },
  {
    path: '/about_app',
    element: <AboutApp />,
  },
  {
    path: '/great_teacher',
    element: <GreatTracher />,
  },
  {
    path: '/price_up',
    element: <PriceUp />,
  },
  {
    path: '/daily_paper',
    element: <DailyPaper />,
  },
  {
    path: 'learning_report_detail',
    element: <LearningReportDetail />,
  },
  {
    path: '/form_teacher',
    element: <FormTeacher />,
  },
  {
    path: '/ny_special',
    element: <NYSpecial />,
  },
  {
    path: '/regular_activity',
    element: <RegularActivity />,
  },
  {
    path: '/space_upgrade',
    element: <SpaceUpgrade />,
  },
  {
    path: '/word_share',
    element: <ShareWord />,
  },
  {
    path: '/expand_space',
    element: <ExpandSpace />,
  },
  {
    path: '/price_diffPay',
    element: <PriceDiffPay />,
  },
  {
    path: '/price_diffPay_hy',
    element: <PriceDiffPayHy />,
  },
  {
    path: '/learning_report',
    element: <LearningReport />,
  },
  {
    path: '/tv',
    element: <TvDownload />,
  },
  {
    path: '/download/tv',
    element: <TvDownload />,
  },
  {
    path: '/booking',
    element: withBookingShell(<BookingPlatform />),
  },
  {
    path: '/booking/intro',
    element: <FilipinoIntro />,
  },
  {
    path: '/booking/category',
    element: <Navigate to="/booking?tab=booking" replace />,
  },
  {
    path: '/booking/teacher/login',
    element: withBookingShell(<TeacherPortalLogin />),
  },
  {
    path: '/booking/teacher/:teacherId',
    element: withBookingShell(<TeacherDetail />),
  },
  {
    path: '/booking/teacher/:teacherId/edit',
    element: withBookingShell(<TeacherEdit />),
  },
  {
    path: '/booking/teacher/add',
    element: withBookingShell(<TeacherAdd />),
  },
  {
    path: '/booking/student-edit',
    element: withBookingShell(<StudentEdit />),
  },
  {
    path: '/booking/course/:courseId',
    element: <Navigate to="/booking?tab=schedule" replace />,
  },
  {
    path: '/booking/course-report/share',
    element: withBookingShell(<CourseReportSharePage />),
  },
  {
    path: '/booking/course-report/:courseRecordId',
    element: withBookingShell(<CourseReportPage />),
  },
  {
    path: '/booking/learning-tasks',
    element: withBookingShell(<LearningTasksPage />),
  },
  {
    path: '/booking/textbook-select',
    element: withBookingShell(<TextbookSelect />),
  },
  {
    path: '/booking/book-detail/:bookId',
    element: withBookingShell(<BookDetail />),
  },
  {
    path: '/booking/confirm',
    element: withBookingShell(<BookingConfirm />),
  },
  {
    path: '/booking/login',
    element: withBookingShell(<BookingLogin />),
  },
  {
    path: '/booking/favorites',
    element: withBookingShell(<FavoritesPage />),
  },
  {
    path: '/booking/feedback',
    element: withBookingShell(<FeedbackPage />),
  },
  {
    path: '/booking/help-center',
    element: withBookingShell(<HelpCenterPage />),
  },
  {
    path: '/booking/teacher-help-center',
    element: withTeacherPortalShell(<TeacherHelpCenterPage />),
  },
  {
    path: '/booking/teacher-system-guide-video',
    element: withTeacherPortalShell(<TeacherSystemGuideVideoPage />),
  },
  {
    path: '/booking/teacher-guide-video',
    element: <Navigate to="/booking/teacher-system-guide-video" replace />,
  },
  {
    path: '/booking/teacher-rules-en',
    element: <TeacherRulesEnPage />,
  },
  {
    path: '/booking/oral-practice',
    element: withBookingShell(<OralPracticeIntroPage />),
  },
  {
    path: '/booking/managed-teachers',
    element: withBookingShell(<ManagedTeachersPage />),
  },
  {
    path: '/booking/managed-teachers/:teacherId',
    element: withBookingShell(<TeacherManagementDetailPage />),
  },
  {
    path: '/booking/points-record',
    element: withBookingShell(<PointsRecordPage />),
  },
  {
    path: '/booking/recharge',
    element: withBookingShell(<RechargePage />),
  },
  {
    path: '/booking/recurring-booking',
    element: withBookingShell(<RecurringBookingPage />),
  },
  {
    path: '/booking/admin-recurring-booking',
    element: withBookingShell(<AdminRecurringBookingPage />),
  },
  {
    path: '/booking/platform-admin',
    element: withBookingShell(<PlatformAdminPage />),
  },
  {
    path: '/booking/welfare-admin',
    element: withBookingShell(<WelfareAdminPage />),
  },
  {
    path: '/booking/user-insight',
    element: withBookingShell(<UserInsightPage />),
  },
  {
    path: '/booking/teacher/schedule',
    element: withTeacherPortalShell(<TeacherSchedulePage />),
  },
  {
    path: '/booking/admin-courses',
    element: withBookingShell(<AdminCoursesPage />),
  },
  {
    path: '/booking/course-material/share',
    element: withTeacherPortalShell(<CourseMaterialSharePage />),
  },
  {
    path: '/booking/teacher-login',
    element: withTeacherPortalShell(<TeacherPortalLogin />),
  },
  {
    path: '/booking/teacher-workbench',
    element: withTeacherPortalShell(<TeacherPortalPage />),
  },
  {
    path: '/booking/teacher-workbench/course/:courseRecordId',
    element: withTeacherPortalShell(<TeacherCourseDetailPage />),
  },
  {
    path: '/raz_interactive',
    element: <RazInteractive />,
  },
  {
    path: '/interactive_word_card',
    element: <InteractiveWordCard />,
  },
]);

// 创建UserContext和Provider
// 创建一个Context
export const UserContext = createContext<DeerUser|undefined>(undefined);

const App = () => {

  const [user, setUser] = useState<DeerUser|undefined>();

  useEffect(() => {
    // ===== 临时修改：booking页面不使用全局的App token逻辑 =====
    // booking页面有自己独立的认证系统，不需要从原生获取用户信息
    const isBookingPage = window.location.pathname.includes('/booking');
    if (isBookingPage) {
      return;
    }
    // ===== 临时修改结束 =====

    if(isApp()){
      // 注册全局消息监听器
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'setUserInfo') {
          if (event.data.token) {
            localStorage.setItem('token', event.data.token);
          }
          if(event.data.user){
            setUser(event.data.user);
          }
        }
      };
      window.addEventListener('message', handleMessage);
      // // 在应用初始化时调用Flutter的获取用户信息的接口
      JsBridge.getUserInfo({ sourceFrom: "h5" });
    }
    // 清理事件监听器
    // return () => {
    //   window.removeEventListener('message', handleMessage);
    // };
  }, []);

  return (
    <React.StrictMode>
      <ErrorBoundary>
        <UserContext.Provider value={user}>
          <Suspense fallback={
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              background: '#fff',
            }}>
              <div style={{ fontSize: '16px', color: '#999' }}>加载中...</div>
            </div>
          }>
            <RouterProvider router={router} />
          </Suspense>
        </UserContext.Provider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
setupChunkLoadRecovery();
root.render(<App />);

//不要等比缩放
// initRem();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
